import { DateTime } from 'luxon';
import { parseISO, isValid, format } from 'date-fns';
import { getAgencyTimezone, PACIFIC_TIMEZONE } from './agencyTimezone';

// Helper function to format date without timezone conversion
export const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Format a Date as YYYYMMDD (e.g. "20260316") for OASIS answers. */
export const formatDateToYYYYMMDD = (date: Date): string =>
  formatDateToISO(date).replace(/-/g, '');

/** Parse a YYYYMMDD string (e.g. "20260316") into a local Date. */
export const parseYYYYMMDD = (str: string): Date | undefined => {
  if (!/^\d{8}$/.test(str)) return undefined;
  const year = parseInt(str.slice(0, 4), 10);
  const month = parseInt(str.slice(4, 6), 10);
  const day = parseInt(str.slice(6, 8), 10);
  const date = new Date(year, month - 1, day);
  return isNaN(date.getTime()) ? undefined : date;
};

// Helper function to format date as M/D/YYYY (not zero padded)
export const formatDateToMMDDYYYY = (
  date: Date | string | null | undefined
): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseISOToLocalDate(date) : date;
  if (!dateObj) return '';

  return new Intl.DateTimeFormat('en-US').format(dateObj);
};

// Helper function to format date as MM/DD/YYYY (zero padded)
export const formatDateToZeroPaddedMMDDYYYY = (
  date: Date | string | null | undefined
): string => {
  if (!date) return '';

  const dateObj = typeof date === 'string' ? parseISOToLocalDate(date) : date;
  if (!dateObj) return '';

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(dateObj);
};

/**
 * Parse a date string as a calendar date (timezone-agnostic).
 * Treats "YYYY-MM-DD" as year/month/day only — no UTC or local time interpretation.
 * Use this (and format with getFullYear/getMonth/getDate) whenever you mean "just a date".
 */
export const parseISOToLocalDate = (
  isoString: string | null | undefined
): Date | undefined => {
  if (!isoString || typeof isoString !== 'string') {
    return undefined;
  }

  const datePart = isoString.split('T')[0];
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (!dateRegex.test(datePart)) {
    return undefined;
  }

  const [year, month, day] = datePart.split('-').map(Number);

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return undefined;
  }

  const date = new Date(year, month - 1, day);

  if (isNaN(date.getTime())) {
    return undefined;
  }

  return date;
};

/**
 * The Monday of the ISO week containing a "YYYY-MM-DD" date, as "YYYY-MM-DD".
 * Returns null when the input is missing or unparseable.
 *
 * Mirrors the backend's `iso_week_bounds` (Monday-start, pure calendar
 * arithmetic, no timezone anywhere) so the client and the server agree on which
 * week a date belongs to. `getDay()` counts from Sunday, so the offset is
 * shifted; the arithmetic goes through the Date constructor rather than
 * `setDate` so month and year rollover are handled by the platform.
 *
 * Used by `useSchedulingPointLoads` as its parse check: a date that yields no
 * week is a date the workload request cannot ask about, so the query stays off.
 * The week itself is not the cache key there — the day being scheduled is.
 */
export const getISOWeekStartISO = (
  dateStr: string | null | undefined
): string | null => {
  const date = parseISOToLocalDate(dateStr);
  if (!date) return null;
  const offsetFromMonday = (date.getDay() + 6) % 7;
  return formatDateToISO(
    new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate() - offsetFromMonday
    )
  );
};

/**
 * DatePicker bounds for logging a patient event within an episode: no earlier
 * than the episode start, no later than the episode end or today, whichever
 * comes first. Missing episode dates relax the corresponding bound.
 */
export const getEpisodeDateBounds = (
  episodeStartDate: string | null | undefined,
  episodeEndDate: string | null | undefined,
  today: Date = getBusinessTodayDate()
): { minDate: Date | undefined; maxDate: Date } => {
  const episodeEnd = parseISOToLocalDate(episodeEndDate);
  return {
    minDate: parseISOToLocalDate(episodeStartDate),
    maxDate: episodeEnd && episodeEnd < today ? episodeEnd : today,
  };
};

// Pacific, for surfaces that stay on Pacific whatever the agency's zone.
const DEFAULT_TIMEZONE = PACIFIC_TIMEZONE;

const formatInstantInZone = (
  dateString: string | null | undefined,
  timeZone: string,
  dateFormat: string
): string => {
  if (!dateString) return '';

  const dt = DateTime.fromISO(dateString, { zone: 'utc' });
  if (!dt.isValid) return '';

  return dt.setZone(timeZone).toFormat(dateFormat);
};

// Helper function to format a datetime string in Pacific Time
// Assumes the input dateString is in UTC (as returned by the backend)
export const formatDateTimeInPT = (
  dateString: string | null | undefined
): string =>
  formatInstantInZone(dateString, DEFAULT_TIMEZONE, 'M/d/yyyy, h:mm a');

/**
 * Same shape as `formatDateTimeInPT`, but in the reader's own timezone, named.
 *
 * For a timestamp whose only job is to tell somebody looking at the screen how
 * long ago something happened. Pacific is right where the reader is comparing
 * against something the business recorded in Pacific; it is wrong for "you
 * pulled this at", which the reader checks against their own clock.
 *
 * The zone is named because the same string is otherwise two different claims
 * depending on who is reading it -- and on a screen whose other stamps are
 * Pacific, an unlabelled local time is the one nobody can place.
 */
export const formatDateTimeLocal = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return '';
  const dt = DateTime.fromISO(dateString, { zone: 'utc' });
  if (!dt.isValid) return '';
  return dt.toLocal().toFormat('M/d/yyyy, h:mm a ZZZZ');
};

/**
 * Same shape as `formatDateTimeInPT`, but in the signed-in agency's timezone
 * (Pacific when the agency has none).
 */
export const formatDateTimeInAgencyZone = (
  dateString: string | null | undefined
): string =>
  formatInstantInZone(dateString, getAgencyTimezone(), 'M/d/yyyy, h:mm a');

/**
 * Same shape as `formatDateTimeInPT`, but rendered in the browser's own
 * timezone.
 *
 * Use where the reader is placing an event against their own clock — a work
 * log of "who changed what, when" reads wrong if a clinician in Texas is shown
 * a Pacific stamp for an edit they made themselves. Assumes the input is UTC,
 * as the backend returns.
 */
export const formatDateTimeInLocalZone = (
  dateString: string | null | undefined
): string => {
  if (!dateString) return '';

  const dt = DateTime.fromISO(dateString, { zone: 'utc' });
  if (!dt.isValid) return '';

  return dt.toLocal().toFormat('M/d/yyyy, h:mm a');
};

/**
 * Same shape as `formatDateTimeInPT`, but in a caller-supplied IANA zone.
 *
 * Use where the reader is comparing the timestamp against something recorded in
 * local clinic time — an agency in Texas reading a Pacific rendering would draw
 * the wrong conclusion. Falls back to Pacific when the zone is missing or not
 * recognised, so a bad value degrades to the app-wide default rather than to an
 * "Invalid DateTime" string. Callers with a different product contract can
 * provide an explicit fallback timezone.
 */
export const formatDateTimeInZone = (
  dateString: string | null | undefined,
  timeZone: string | null | undefined,
  fallbackTimeZone: string = DEFAULT_TIMEZONE,
  dateFormat = 'M/d/yyyy, h:mm a'
): string => {
  if (!dateString) return '';

  const dt = DateTime.fromISO(dateString, { zone: 'utc' });
  if (!dt.isValid) return '';

  const fallback = dt.setZone(fallbackTimeZone);
  const zoned = timeZone ? dt.setZone(timeZone) : fallback;
  const resolved = zoned.isValid ? zoned : fallback.isValid ? fallback : dt;

  return resolved.toFormat(dateFormat);
};

// Format a UTC ISO datetime string as MM/DD/YYYY in Pacific Time.
// Use for backend datetime fields (timestamp with time zone) — never for
// plain YYYY-MM-DD calendar dates, which carry no timezone to convert.
export const formatDateInPT = (dateString: string | null | undefined): string =>
  formatInstantInZone(dateString, DEFAULT_TIMEZONE, 'MM/dd/yyyy');

/**
 * Same shape as `formatDateInPT`, but in the signed-in agency's timezone
 * (Pacific when the agency has none).
 */
export const formatDateInAgencyZone = (
  dateString: string | null | undefined
): string => formatInstantInZone(dateString, getAgencyTimezone(), 'MM/dd/yyyy');

// Format a UTC ISO datetime string as a 12-hour Pacific clock time
// (e.g. "3:42 PM"). Use for freshness stamps where the day is implied;
// pair with formatDateTimeInPT when the calendar day matters.
export const formatTimeInPT = (dateString: string | null | undefined): string =>
  formatInstantInZone(dateString, DEFAULT_TIMEZONE, 'h:mm a');

/**
 * Same shape as `formatTimeInPT`, but in the signed-in agency's timezone
 * (Pacific when the agency has none).
 */
export const formatTimeInAgencyZone = (
  dateString: string | null | undefined
): string => formatInstantInZone(dateString, getAgencyTimezone(), 'h:mm a');

// Helper function to get today's date in Pacific Time as YYYY-MM-DD
export const getTodayInPT = (): string => {
  return DateTime.now().setZone(DEFAULT_TIMEZONE).toISODate() || '';
};

/**
 * Today on the signed-in agency's business calendar, as `YYYY-MM-DD`.
 *
 * The server dates agency business records (`agency_business_date_today()`)
 * in the agency's timezone, falling back to Pacific. Defaults and "today"
 * checks that the server validates must use the same day, or a browser in
 * another zone is offered a date the server rejects.
 */
export const getBusinessToday = (): string =>
  DateTime.now().setZone(getAgencyTimezone()).toISODate() || '';

// Milliseconds from `now` until the start of the next Pacific calendar day.
// Use to schedule a timer that re-renders day-relative labels (e.g. the "Data
// as of h:mm a" freshness stamp) the instant the Pacific day rolls over, so a
// tab left open across midnight flips a dateless bare time to a date-prefixed
// label instead of holding a stale, ambiguous one.
export const msUntilNextPTMidnight = (
  now: DateTime = DateTime.now()
): number => {
  const nowPT = now.setZone(DEFAULT_TIMEZONE);
  const nextMidnight = nowPT.plus({ days: 1 }).startOf('day');
  return Math.max(0, nextMidnight.toMillis() - nowPT.toMillis());
};

/**
 * `getBusinessToday` as a local-midnight Date, comparable directly with
 * `parseISOToLocalDate` results. Use for "today" comparisons and date-picker
 * bounds that must agree with backend validation; a browser-local
 * `new Date()` can be a day off from the agency's calendar.
 */
export const getBusinessTodayDate = (): Date =>
  parseISOToLocalDate(getBusinessToday()) ?? new Date();

/** Return the day after the given YYYY-MM-DD string as a Date, or undefined if invalid. */
export const getDayAfterDate = (
  dateStr: string | null | undefined
): Date | undefined => {
  const d = parseISOToLocalDate(dateStr ?? '');
  if (!d) return undefined;
  const next = new Date(d);
  next.setDate(next.getDate() + 1);
  return next;
};

/**
 * Return the day after the given YYYY-MM-DD string as YYYY-MM-DD, or null if invalid.
 * Purely calendar-date arithmetic (timezone-agnostic). Use for min-date logic.
 */
export const getDayAfterDateStr = (
  dateStr: string | null | undefined
): string | null => {
  const next = getDayAfterDate(dateStr);
  if (!next) return null;
  return formatDateToISO(next);
};

// Helper function to calculate age from date of birth
export const calculateAge = (dob: string | null | undefined): number | null => {
  if (!dob) return null;
  const birthDate = parseISOToLocalDate(dob);
  if (!birthDate) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();

  const birthdayThisYear = new Date(
    today.getFullYear(),
    birthDate.getMonth(),
    birthDate.getDate()
  );
  if (today < birthdayThisYear) {
    age--;
  }

  return age;
};

// Sort an array of objects by a date-string key using local date parsing
export function sortByLocalDate<T>(
  items: T[],
  key: {
    [K in keyof T]: T[K] extends string | null | undefined ? K : never;
  }[keyof T],
  direction: 'asc' | 'desc' = 'asc'
): T[] {
  return [...items].sort((a, b) => {
    const valA = a[key] as string | null | undefined;
    const valB = b[key] as string | null | undefined;
    const dateA =
      parseISOToLocalDate(valA as string | null | undefined)?.getTime() ?? 0;
    const dateB =
      parseISOToLocalDate(valB as string | null | undefined)?.getTime() ?? 0;
    return direction === 'asc' ? dateA - dateB : dateB - dateA;
  });
}

// Helper function to generate diagnosis log message with timestamp
// Returns string like "Manually added diagnosis code via dashboard at 01/06/2026, 12:34:56 PM GMT-08:00."
export const getManualDiagnosisLogMessage = (
  date: Date = new Date()
): string => {
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'longOffset',
  }).format(date);
  return `Manually added diagnosis code via dashboard at ${formattedDate}.`;
};

/**
 * Formats a date string for display (e.g. "Mar 5, 2026").
 */
export const formatDateForDisplay = (dateString: string): string => {
  const date = parseISOToLocalDate(dateString);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a date string with its weekday (e.g. "Mon, Mar 5").
 *
 * For a date the reader is placing inside a week they already have in view —
 * the year is redundant there, and the weekday is the part that locates the day
 * against the rest of the week.
 */
export const formatWeekdayAndDate = (dateString: string): string => {
  const date = parseISOToLocalDate(dateString);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Formats a clock-time string ("HH:MM" or "HH:MM:SS") as a 12-hour time
 * (e.g. "8:30 AM"). Returns '' when absent or unparseable.
 */
export const formatTimeOfDay = (value?: string | null): string => {
  if (!value) return '';
  const [hStr, mStr] = value.split(':');
  const hour = Number(hStr);
  const minute = Number(mStr);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return '';
  // Defensively reject out-of-range values rather than rendering nonsense
  // like "1:99 PM" for a malformed input.
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return '';
  const period = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${period}`;
};

/**
 * Formats a datetime string, falling back to UTC if no timezone is provided.
 */
export const formatDateTimeWithTimezoneFallback = (
  dateString: string | null | undefined,
  dateFormat: string = 'MMM d, yyyy h:mm a'
): string => {
  if (!dateString) return '';
  try {
    // Append Z to treat as UTC if it doesn't have timezone info
    let withZ = dateString;
    if (!dateString.endsWith('Z') && !dateString.match(/[+-]\d{2}:\d{2}$/)) {
      withZ = `${dateString}Z`;
    }
    const date = parseISO(withZ);
    if (!isValid(date)) return '';
    return format(date, dateFormat);
  } catch {
    return '';
  }
};

/**
 * Whole calendar days from start to end, measured in the browser's local
 * timezone. Both arguments are Date instants; the caller constructs them so the
 * timezone intent is explicit — e.g. `new Date(iso)` for a UTC timestamp (the
 * local calendar date of that instant) or `parseISOToLocalDate` for a
 * calendar-date string. Negative when end precedes start.
 */
export const daysBetween = (start: Date, end: Date): number => {
  const startLocal = new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate()
  );
  const endLocal = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((endLocal.getTime() - startLocal.getTime()) / MS_PER_DAY);
};

/**
 * Format a signed minute count as a human-readable gap, e.g. `+12 min`,
 * `−1h 15m`, `+63d`. Returns an em dash for null/undefined, because "no gap to
 * measure" is meaningful and must not read as zero.
 *
 * Larger magnitudes get coarser units on purpose: shared by the EVV readings
 * panel and the planned EVV Discrepancies report (EMR-325), where a reading
 * landing days from the visit date is the loudest signal there is and
 * `+90724 min` would bury it. Minutes stay exact below an hour — the range an
 * ordinary late arrival falls in.
 */
export const formatSignedMinutes = (
  minutes: number | null | undefined
): string => {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes === 0) return 'on time';
  const sign = minutes > 0 ? '+' : '−';
  const total = Math.abs(minutes);
  if (total < 60) return `${sign}${total} min`;
  if (total < 60 * 24) {
    const h = Math.floor(total / 60);
    const m = total % 60;
    return m === 0 ? `${sign}${h}h` : `${sign}${h}h ${m}m`;
  }
  const d = Math.floor(total / (60 * 24));
  const h = Math.floor((total % (60 * 24)) / 60);
  return h === 0 ? `${sign}${d}d` : `${sign}${d}d ${h}h`;
};
